import _ from "lodash";
import React, { useState, useMemo, useEffect } from "react";
import firebase from "firebase/app";
import "firebase/auth";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { fade } from "@material-ui/core/styles/colorManipulator";
import CloseIcon from "@material-ui/icons/Close";
import { useAlert } from "react-alert";
import PerfectScrollbar from "react-perfect-scrollbar";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import * as Sentry from "@sentry/browser";

import AddIcon from "@material-ui/icons/AddOutlined";

import Navigation from "./Navigation";
import { makeRequest, specifiedUserIsFPEmployee } from "./util";
import Label from "./Label";
import useAccountMetadata from "./hooks/useAccountMetadata";
import useUsers from "./hooks/useUsers";
import FreshpaintUser from "./components/users/FreshpaintUser";
import CheckboxWithDisableTooltip from "./components/CheckboxWithDisableTooltip";
import { useDispatch } from "react-redux";
import { fetchUsers } from "./actions/users";
import useFeatureFlags from "./hooks/useFeatureFlag";
import DeleteInviteModal from "./DeleteInviteModal";

const RoleAdmin = "admin";
const RoleNonAdmin = "user";

function getToggledRole(currentRole) {
  if (currentRole === RoleNonAdmin) {
    return RoleAdmin;
  }
  return RoleNonAdmin;
}

const useTableStyles = makeStyles((theme) => ({
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  inner: {
    minWidth: 700,
  },
  hidden: {
    visibility: "hidden",
  },
  card: {
    width: "65%",
    minWidth: 800,
  },
  clickableImg: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  navigation: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  bold: {
    fontWeight: 550,
  },
  deleteEventButton: {
    color: colors.red[600],
    border: `1px solid ${fade(colors.red[600], 0.5)}`,
    "&:hover": {
      border: `1px solid ${colors.red[600]}`,
      backgroundColor: fade(colors.red[600], theme.palette.action.hoverOpacity),
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  topbar: {
    display: "flex",
    backgroundColor: "white",
    width: "100%",
    padding: "10px",
    justifyContent: "flex-end",
  },
  body: {
    paddingLeft: 60,
    paddingTop: 45,
  },
  member: {
    "&:hover": {
      backgroundColor: "rgba(38, 50, 56, 0.08)",
    },
  },
}));

function TeamPage() {
  const classes = useTableStyles();
  const alert = useAlert();
  const dispatch = useDispatch();

  const accountMetadata = useAccountMetadata();
  const flag = useFeatureFlags();
  const usersState = useUsers();

  const [inviteUserEmail, setInviteUserEmail] = useState(null);
  const [inviteUserRole, setInviteUserRole] = useState(RoleAdmin);
  const [inviteSelectedEnvs, setInviteSelectedEnvs] = useState(null);

  const [userToDelete, setUserToDelete] = useState(null);

  const [inviteToDelete, setInviteToDelete] = useState(null);

  const invites = useMemo(() => {
    if (usersState.loading) {
      return [];
    }
    return _(usersState.invitedUsers)
      .sortBy((invite) => invite.to_email)
      .value();
  }, [usersState]);

  const members = useMemo(() => {
    if (usersState.loading) {
      return null;
    }

    const you = usersState.users.filter(
      (user) => user.email === firebase.auth().currentUser.email
    );
    const otherUsers = _(usersState.users)
      .filter((user) => user.email !== firebase.auth().currentUser.email)
      .sortBy((user) => user.email)
      .value();

    return you.concat(otherUsers);
  }, [usersState]);

  const currentMember = useMemo(() => {
    let you;
    if (!usersState.loading) {
      you = usersState.users.find(
        (member) => member.uid === firebase.auth().currentUser.uid
      );
    }
    return new FreshpaintUser(you);
  }, [usersState]);

  const [expandedMembers, setExpandedMembers] = useState({});

  const [loadingUserEnvs, setLoadingUserEnvs] = useState(true);
  const [userEnvs, setUserEnvs] = useState(null);

  function fetchUserEnvs() {
    makeRequest("get_all_non_admin_envs")
      .then((response) => {
        const envUsersList = response.data;
        const out = {};
        for (let { uid, proj_id, env_id } of envUsersList) {
          const projMap = out[uid] || {};
          const envMap = projMap[proj_id] || {};
          envMap[env_id] = true;
          projMap[proj_id] = envMap;
          out[uid] = projMap;
        }
        setUserEnvs(out);
        setLoadingUserEnvs(false);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  useEffect(() => {
    fetchUserEnvs();
  }, []);

  function handleUserEnvAccessUpdate(uid, projectID, envID, hasAccess) {
    makeRequest("update_user_env", {
      uid,
      proj_id: projectID,
      env_id: envID,
      has_access: hasAccess,
    })
      .then(() => {
        fetchUserEnvs();
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleInviteUser() {
    if (!inviteUserEmail) {
      alert.error("You must provide an email to send invite");
      return;
    }

    let envIDs;
    if (inviteUserRole === RoleAdmin) {
      envIDs = [];
    } else {
      if (!inviteSelectedEnvs) {
        // request access to all environments
        const projects = accountMetadata.projects.filter(
          (project) => project.environments.length
        );
        envIDs = _.flatten(
          projects.map((project) => project.environments.map((env) => env.ID))
        );
      } else {
        envIDs = _(inviteSelectedEnvs)
          .entries()
          .filter((e) => e[1])
          .map((e) => e[0])
          .value();
      }
    }
    const userEnvs = envIDs.map((envID) => ({ env_id: envID }));

    makeRequest("invite_user", {
      email: inviteUserEmail,
      role: inviteUserRole,
      user_envs: userEnvs,
    })
      .then(() => {
        setInviteUserEmail(null);
        setInviteUserRole(RoleAdmin);
        setInviteSelectedEnvs(null);
        dispatch(fetchUsers());
        alert.success("Invite Sent");
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleDeleteInvite() {
    makeRequest("remove_user_invite", { invite_id: inviteToDelete.id })
      .then(() => {
        dispatch(fetchUsers());
        setInviteToDelete(null);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to remove invite.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleDeleteUser() {
    makeRequest("remove_user", { uid: userToDelete.uid })
      .then(() => {
        dispatch(fetchUsers());
        setUserToDelete(null);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleAdminToggle(toggledMember) {
    makeRequest("update_user_role", {
      uid: toggledMember.uid,
      role: getToggledRole(toggledMember.role),
    })
      .then(() => {
        dispatch(fetchUsers());
      })
      .catch((error) => {
        if (error.response.status === 403) {
          alert.error(
            "You're not authorized to perform this action. Please reload page."
          );
          return;
        }
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleMemberRowClick(clickedMember) {
    const { uid } = clickedMember;
    setExpandedMembers((prevExpandedMembers) => ({
      ...prevExpandedMembers,
      [uid]: !prevExpandedMembers[uid],
    }));
  }

  return (
    <>
      <div className={clsx(classes.topbar)}>
        <Button
          className={clsx(
            flag("rbac") && !currentMember.hasAdminAccess() && classes.hidden
          )}
          color="primary"
          variant="contained"
          onClick={() => setInviteUserEmail("")}
        >
          <AddIcon className={classes.icon} />
          ADD TEAMMATE
        </Button>
      </div>
      {invites.length > 0 && (
        <InvitesList
          currentMember={currentMember}
          invites={invites}
          onClickDelete={setInviteToDelete}
        />
      )}
      {members && (
        <MemberList
          currentMember={currentMember}
          members={members}
          onClickDelete={setUserToDelete}
          onAdminToggle={handleAdminToggle}
          onClickMemberRow={handleMemberRowClick}
          expandedMembers={expandedMembers}
          loadingUserEnvs={loadingUserEnvs}
          userEnvs={userEnvs}
          onUserEnvAccessUpdate={handleUserEnvAccessUpdate}
        />
      )}
      <InviteUserModal
        open={inviteUserEmail !== null}
        onClose={() => setInviteUserEmail(null)}
        onInvite={handleInviteUser}
        onEmailChange={setInviteUserEmail}
        inviteUserRole={inviteUserRole}
        onToggleRole={() => {
          setInviteUserRole((currentRole) => {
            if (currentRole === RoleNonAdmin) {
              return RoleAdmin;
            }
            return RoleNonAdmin;
          });
        }}
        inviteSelectedEnvs={inviteSelectedEnvs}
        setInviteSelectedEnvs={setInviteSelectedEnvs}
      />
      <DeleteUserModal
        open={userToDelete !== null}
        user={userToDelete}
        onClose={() => setUserToDelete(null)}
        onDelete={handleDeleteUser}
      />
      <DeleteInviteModal
        open={inviteToDelete !== null}
        invite={inviteToDelete}
        onClose={() => setInviteToDelete(null)}
        onDelete={handleDeleteInvite}
      />
    </>
  );
}

const MemberList = React.memo(function MemberList({
  currentMember,
  members,
  onClickDelete,
  onAdminToggle,
  onClickMemberRow,
  expandedMembers,
  loadingUserEnvs,
  userEnvs,
  onUserEnvAccessUpdate,
}) {
  const classes = useTableStyles();
  const flag = useFeatureFlags();

  return (
    <div className={clsx(classes.body)}>
      <Card className={clsx(classes.card)}>
        <CardHeader title="Teammates" />
        <Divider />
        <CardContent className={clsx(classes.content)}>
          <PerfectScrollbar>
            <div className={clsx(classes.inner)}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Email</TableCell>
                    {flag("rbac") && <TableCell>Admin</TableCell>}
                    <TableCell align="center">Created At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((member) => {
                    const tableRowExtraProps = flag("rbac")
                      ? {
                          onClick: () => {
                            onClickMemberRow(member);
                          },
                        }
                      : {};

                    return (
                      <>
                        <TableRow
                          key={member.email}
                          className={clsx(
                            classes.member,
                            { "cursor-pointer": flag("rbac") },
                            {
                              "border-b-0 bg-gray-100":
                                expandedMembers[member.uid],
                            }
                          )}
                          {...tableRowExtraProps}
                        >
                          <TableCell>
                            {member.uid === currentMember.uid && (
                              <Label color="#3f51b5">You</Label>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={clsx(classes.bold)}>
                              {member.email}
                            </span>
                          </TableCell>
                          {flag("rbac") && (
                            <TableCell>
                              <CheckboxWithDisableTooltip
                                checked={member.role === RoleAdmin}
                                onChange={() => {
                                  onAdminToggle(member);
                                }}
                                getIsDisabled={() => {
                                  if (!currentMember.hasAdminAccess()) {
                                    return [true, "You are not admin"];
                                  }
                                  if (member.uid === currentMember.uid) {
                                    return [true, "Can't change self role"];
                                  }
                                  if (specifiedUserIsFPEmployee(member.uid)) {
                                    return [true, "Member is a super-user"];
                                  }
                                  return [false, null];
                                }}
                              />
                            </TableCell>
                          )}
                          <TableCell align="center">
                            {moment(member.created_at).format("MMMM D, YYYY")}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              className={clsx(
                                classes.deleteEventButton,
                                (member.uid === currentMember.uid ||
                                  !currentMember.hasAdminAccess()) &&
                                  classes.hidden
                              )}
                              onClick={() => onClickDelete(member)}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                        {expandedMembers[member.uid] && (
                          <TableRow>
                            <TableCell colSpan={9999}>
                              <UserEnvironmentSelectorUsersTable
                                member={member}
                                loadingUserEnvs={loadingUserEnvs}
                                userEnvs={userEnvs}
                                enableToggle={currentMember.hasAdminAccess()}
                                onUserEnvAccessUpdate={onUserEnvAccessUpdate}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
      </Card>
    </div>
  );
});

MemberList.propTypes = {
  currentMember: PropTypes.object.isRequired,
  members: PropTypes.array.isRequired,
  onClickDelete: PropTypes.func.isRequired,
  onAdminToggle: PropTypes.func.isRequired,
  onClickMemberRow: PropTypes.func.isRequired,
  expandedMembers: PropTypes.object.isRequired,
  loadingUserEnvs: PropTypes.bool.isRequired,
  userEnvs: PropTypes.object,
  onUserEnvAccessUpdate: PropTypes.func.isRequired,
};

const InvitesList = function InvitesList({
  currentMember,
  invites,
  onClickDelete,
}) {
  const classes = useTableStyles();
  return (
    <div className={clsx(classes.body)}>
      <Card className={clsx(classes.card)}>
        <CardHeader title="Invited Teammates" />
        <Divider />
        <CardContent className={clsx(classes.content)}>
          <PerfectScrollbar>
            <div className={clsx(classes.inner)}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Created At</TableCell>
                    {currentMember.hasAdminAccess() && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invites.map((invite) => {
                    return (
                      <TableRow key={invite.to_email}>
                        <TableCell>
                          <Label color="#3f51b5">Pending</Label>
                        </TableCell>
                        <TableCell>
                          <span className={clsx(classes.bold)}>
                            {invite.to_email}
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          {moment(invite.created_at).format("MMMM D, YYYY")}
                        </TableCell>
                        {currentMember.hasAdminAccess() && (
                          <TableCell align="center">
                            <Button
                              className={clsx(classes.deleteEventButton)}
                              onClick={() => onClickDelete(invite)}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
      </Card>
    </div>
  );
};

InvitesList.propTypes = {
  currentMember: PropTypes.object.isRequired,
  invites: PropTypes.array.isRequired,
  onClickDelete: PropTypes.func.isRequired,
};

const userEnvSelectorStyles = makeStyles(() => ({
  textCell: {
    textAlign: "right",
  },
  checkboxCell: {
    textAlign: "center",
  },

  noborder: {
    border: "none",
  },

  hidden: {
    visibility: "hidden",
  },
}));

const UserEnvironmentSelectorBase = ({
  getHasAccess,
  updateAccess,
  enableToggle,
  showAllProjectsEnvs,
  showProjectCheckbox,
}) => {
  const accountMetadata = useAccountMetadata();
  const classes = userEnvSelectorStyles();

  const projects = accountMetadata.projects.filter(
    (project) => project.environments.length
  );

  const hasAccessProjAllEnvs = {};
  for (let project of projects) {
    hasAccessProjAllEnvs[project.ID] = _.every(project.environments, (env) =>
      getHasAccess(project.ID, env.ID)
    );
  }
  const hasAccessAllProjsAndEnvs = _.every(hasAccessProjAllEnvs);

  return (
    <Table size="small" padding="none">
      {showAllProjectsEnvs && (
        <TableRow>
          <TableCell className={classes.textCell}>
            <i>All Projects and Environments</i>
          </TableCell>
          <TableCell className={classes.checkboxCell}>
            <Checkbox
              checked={hasAccessAllProjsAndEnvs}
              disabled={!enableToggle}
              onChange={() => {
                updateAccess(null, null, !hasAccessAllProjsAndEnvs);
              }}
            />
          </TableCell>
        </TableRow>
      )}
      {projects.map((project, i) => {
        const isLastProj = i === projects.length - 1;
        const hasAccessAllEnvs = hasAccessProjAllEnvs[project.ID];

        return (
          <>
            <TableRow>
              <TableCell className={clsx(classes.textCell, classes.noborder)}>
                <b>{project.name}</b>
              </TableCell>
              <TableCell
                className={clsx(classes.checkboxCell, classes.noborder)}
              >
                <Checkbox
                  className={clsx(!showProjectCheckbox && classes.hidden)}
                  checked={hasAccessAllEnvs}
                  disabled={!enableToggle}
                  onChange={() => {
                    updateAccess(project.ID, null, !hasAccessAllEnvs);
                  }}
                />
              </TableCell>
            </TableRow>
            {_.sortBy(project.environments, "createdAt").map(
              (environment, j) => {
                const isLastEnv = j === project.environments.length - 1;
                const showBorder = !isLastProj && isLastEnv;
                const hasAccess = getHasAccess(project.ID, environment.ID);

                return (
                  <>
                    <TableRow>
                      <TableCell
                        className={clsx(classes.textCell, {
                          [classes.noborder]: !showBorder,
                        })}
                      >
                        {environment.name}
                      </TableCell>
                      <TableCell
                        className={clsx(classes.checkboxCell, {
                          [classes.noborder]: !showBorder,
                        })}
                      >
                        <Checkbox
                          checked={hasAccess}
                          disabled={!enableToggle}
                          onChange={() => {
                            updateAccess(
                              project.ID,
                              environment.ID,
                              !hasAccess
                            );
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </>
                );
              }
            )}
          </>
        );
      })}
    </Table>
  );
};

const UserEnvironmentSelectorUsersTable = ({
  member,
  loadingUserEnvs,
  userEnvs,
  enableToggle,
  onUserEnvAccessUpdate,
}) => {
  const accountMetadata = useAccountMetadata();

  const isAdmin = member.role === RoleAdmin;
  if (!isAdmin && loadingUserEnvs) {
    return <>Loading</>;
  }

  const getHasAccess = (projID, envID) => {
    if (isAdmin) {
      return true;
    }
    const projMap = userEnvs[member.uid] || {};
    const envMap = projMap[projID] || {};
    return !!envMap[envID];
  };

  const updateAccess = (projID, envID, hasAccess) => {
    onUserEnvAccessUpdate(member.uid, projID, envID, hasAccess);
  };

  return (
    <UserEnvironmentSelectorBase
      getHasAccess={getHasAccess}
      updateAccess={updateAccess}
      enableToggle={!isAdmin && enableToggle}
      showAllProjectsEnvs={enableToggle && accountMetadata.projects.length > 0}
      showProjectCheckbox={enableToggle}
    />
  );
};

const UserEnvironmentSelectorInviteModal = ({
  inviteSelectedEnvs,
  setInviteSelectedEnvs,
}) => {
  const accountMetadata = useAccountMetadata();
  const projects = accountMetadata.projects.filter(
    (project) => project.environments.length
  );

  const getHasAccess = (_, envID) => {
    if (!inviteSelectedEnvs) {
      return true;
    }
    return !!inviteSelectedEnvs[envID];
  };

  const updateAccess = (projID, envID, hasAccess) => {
    let updatedEnvIDs;
    if (projID && envID) {
      updatedEnvIDs = [envID];
    } else {
      if (projID) {
        const project = projects.find((project) => project.ID === projID);
        if (project) {
          updatedEnvIDs = project.environments.map((env) => env.ID);
        }
      } else {
        updatedEnvIDs = _.flatten(
          projects.map((project) => project.environments.map((env) => env.ID))
        );
      }
    }
    setInviteSelectedEnvs((prevEnvs) => {
      let envs;
      if (prevEnvs) {
        envs = { ...prevEnvs };
      } else {
        const allEnvIDs = _.flatten(
          projects.map((project) => project.environments.map((env) => env.ID))
        );
        envs = {};
        for (let envID of allEnvIDs) {
          envs[envID] = true;
        }
      }

      for (let envID of updatedEnvIDs) {
        envs[envID] = hasAccess;
      }
      return envs;
    });
  };

  return (
    <UserEnvironmentSelectorBase
      getHasAccess={getHasAccess}
      updateAccess={updateAccess}
      enableToggle={true}
      showAllProjectsEnvs={true}
      showProjectCheckbox={true}
    />
  );
};

const useModalStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    boxShadow: theme.shadows[20],
    width: 500,
    maxHeight: "100%",
    overflowY: "auto",
    maxWidth: "100%",
  },
  container: {
    marginTop: theme.spacing(3),
    height: 200,
  },
  actions: {
    justifyContent: "flex-end",
  },
  inputField: {
    marginTop: 16,
    overflowX: "wrap",
    wordBreak: "break-all",
  },
  deactivateButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.red[600],
    "&:hover": {
      backgroundColor: colors.red[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.red[600],
      },
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  bold: {
    fontWeight: 550,
  },
}));

function InviteUserModal({
  open,
  onClose,
  onEmailChange,
  inviteUserRole,
  onToggleRole,
  onInvite,
  inviteSelectedEnvs,
  setInviteSelectedEnvs,
}) {
  const classes = useModalStyles();
  const flag = useFeatureFlags();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Invite Teammate"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <TextField
            fullWidth
            id="invite-user-email"
            label="Email"
            name="invite-user-email"
            variant="outlined"
            className={clsx(classes.inputField)}
            onChange={(e) => onEmailChange(e.target.value)}
          />
          {flag("rbac") && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={inviteUserRole === RoleAdmin}
                    onChange={() => onToggleRole()}
                  />
                }
                label="Admin"
              />
              {inviteUserRole !== RoleAdmin && (
                <UserEnvironmentSelectorInviteModal
                  inviteSelectedEnvs={inviteSelectedEnvs}
                  setInviteSelectedEnvs={setInviteSelectedEnvs}
                />
              )}
            </>
          )}
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>Close</Button>
          <Button color="primary" onClick={onInvite} variant="contained">
            Send Invite
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

InviteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInvite: PropTypes.func.isRequired,
  onEmailChange: PropTypes.func.isRequired,
  inviteUserRole: PropTypes.string.isRequired,
  onToggleRole: PropTypes.func.isRequired,
  inviteSelectedEnvs: PropTypes.object,
  setInviteSelectedEnvs: PropTypes.func.isRequired,
};

function DeleteUserModal({ open, user, onClose, onDelete }) {
  const classes = useModalStyles();

  const userString = user ? user.email : "this user";

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Invite Teammate"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            <span className={clsx(classes.bold)}>
              Are you sure you want to delete {userString} from your account?
            </span>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>Close</Button>
          <Button
            className={classes.deactivateButton}
            onClick={onDelete}
            variant="contained"
          >
            Delete User
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

DeleteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const navigationStyles = makeStyles(() => ({
  navigation: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

function TeamPageWithNavigation() {
  const classes = navigationStyles();

  return (
    <Navigation className={classes.navigation}>
      <TeamPage />
    </Navigation>
  );
}

export default TeamPageWithNavigation;
